import { LoaderCircle, Send, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { askAssistant, extractErrorMessage } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";

const ResultsTable = ({ columns, results, navigate }) => {
  if (!results?.length) {
    return null;
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-200" data-testid="assistant-results-table">
      <table className="w-full text-left text-xs">
        <thead className="bg-zinc-50 text-zinc-500">
          <tr>{columns.map((column) => <th className="px-3 py-2 font-semibold uppercase tracking-[0.08em]" key={column}>{column.replace(/_/g, " ")}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {results.map((row, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td className="px-3 py-2 text-zinc-700" key={column}>
                  {column === "load_number" && row[column] ? (
                    <button className="font-semibold text-zinc-900 underline" data-testid={`assistant-result-load-link-${row[column]}`} onClick={() => navigate(`/loads?q=${encodeURIComponent(row[column])}`)} type="button">{row[column]}</button>
                  ) : String(row[column] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AssistantPanel = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);

  const handleAsk = async (event) => {
    event.preventDefault();
    const trimmed = question.trim();
    if (!trimmed || asking) {
      return;
    }
    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((current) => [...current, { role: "user", content: trimmed }]);
    setQuestion("");
    setAsking(true);
    try {
      const result = await askAssistant(trimmed, history);
      setMessages((current) => [...current, { role: "assistant", content: result.answer, results: result.results, columns: result.columns, configured: result.configured }]);
    } catch (error) {
      const message = extractErrorMessage(error, "The assistant couldn't answer that right now.");
      toast.error(message);
      setMessages((current) => [...current, { role: "assistant", content: message }]);
    } finally {
      setAsking(false);
    }
  };

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="assistant-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> AI Assistant</SheetTitle>
        </SheetHeader>

        <div className="mt-4 flex-1 space-y-3 overflow-y-auto pr-1" data-testid="assistant-message-list">
          {messages.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-zinc-200 px-4 py-6 text-center text-sm text-zinc-500">
              Ask me about uninvoiced loads, revenue by customer, upcoming safety due dates, loads by status, or overdue invoices.
            </p>
          ) : null}
          {messages.map((message, index) => (
            <div className={message.role === "user" ? "ml-6 rounded-2xl bg-zinc-900 px-4 py-2 text-sm text-white" : "mr-6 space-y-2 rounded-2xl bg-zinc-100 px-4 py-2 text-sm text-zinc-800"} data-testid={`assistant-message-${index}`} key={index}>
              <p>{message.content}</p>
              {message.role === "assistant" ? <ResultsTable columns={message.columns} navigate={navigate} results={message.results} /> : null}
            </div>
          ))}
          {asking ? <div className="mr-6 flex items-center gap-2 rounded-2xl bg-zinc-100 px-4 py-2 text-sm text-zinc-500" data-testid="assistant-loading"><LoaderCircle className="h-4 w-4 animate-spin" /> Thinking...</div> : null}
        </div>

        <form className="mt-4 flex gap-2" onSubmit={handleAsk}>
          <input className="flex-1 rounded-2xl border border-zinc-200 px-4 py-3 text-sm" data-testid="assistant-question-input" onChange={(event) => setQuestion(event.target.value)} placeholder="Ask about loads, invoices, revenue..." value={question} />
          <button className="inline-flex items-center justify-center rounded-2xl bg-zinc-900 px-4 py-3 text-white disabled:cursor-not-allowed disabled:opacity-60" data-testid="assistant-send-button" disabled={asking || !question.trim()} type="submit"><Send className="h-4 w-4" /></button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
